import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, Upload } from 'lucide-react';
import { createShareLinks } from '../../config';

/**
 * EventsTab Component
 * Display events with category filters, upcoming horizontal scroll, and all events grid
 * Matching native app design from EventsSection.tsx
 */
const EventsTab = ({ community, events }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isInIframe, setIsInIframe] = useState(false);
  const upcomingScrollRef = useRef(null);
  
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);
  
  const handleEventClick = (eventId) => {
    // Always navigate to public event view, ignoring authentication status
    // This ensures public community events always show the public details page
    navigate(`/group/${community.id}/event/${eventId}`);
  };

  // Handle share event
  const handleShareEvent = async (e, eventId) => {
    e.stopPropagation(); // Prevent card click navigation
    
    const shareUrl = createShareLinks.event(eventId);
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      showCopyToast();
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showCopyToast();
      } catch (fallbackErr) {
        console.error('Failed to copy link');
      }
    }
  };

  // Show copy success toast
  const showCopyToast = () => {
    const existingToast = document.querySelector('.copy-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = 'Link copied!';
    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast && toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 3000);
  };

  // Format date and time
  const formatEventDateTime = (event) => {
    const dateStr = event.displayDate || event.date || event.event_date;
    if (!dateStr) return { date: '', time: '' };
    
    try {
      const eventDate = new Date(dateStr);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
      
      let dateString = '';
      if (eventDateOnly.getTime() === today.getTime()) {
        dateString = 'Today';
      } else if (eventDateOnly.getTime() === tomorrow.getTime()) {
        dateString = 'Tomorrow';
      } else {
        dateString = eventDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        });
      }
      
      const timeString = eventDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return { date: dateString, time: timeString };
    } catch (error) {
      return { date: '', time: '' };
    }
  };

  // Format price display
  const formatPrice = (amount) => {
    if (amount % 1 === 0) {
      return amount.toFixed(0);
    }
    return amount.toFixed(2);
  };

  const getPriceDisplay = (event) => {
    if (!event.pricing_type || event.pricing_type === 'free') {
      return 'Free';
    }
    
    if (event.pricing_type === 'flat' && event.ticket_price_cents !== undefined && event.ticket_price_cents !== null) {
      const dollars = event.ticket_price_cents / 100;
      if (dollars === 0) return 'Free';
      return `$${formatPrice(dollars)}`;
    }
    
    if (event.pricing_type === 'tiered' && event.pricing_rules && event.pricing_rules.length > 0) {
      const prices = event.pricing_rules
        .map(rule => {
          const priceCents = rule.price_cents || rule.priceCents;
          if (typeof priceCents === 'string') {
            return parseFloat(priceCents);
          }
          return priceCents || 0;
        })
        .filter(price => !isNaN(price));
      
      if (prices.length > 0) {
        const minPrice = Math.min(...prices) / 100;
        const maxPrice = Math.max(...prices) / 100;
        
        if (maxPrice === 0) return 'Free';
        if (minPrice === maxPrice) return `$${formatPrice(minPrice)}`;
        return `$${formatPrice(minPrice)}-$${formatPrice(maxPrice)}`;
      }
    }
    
    return 'Free';
  };

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    events.forEach(event => {
      if (event.category) {
        uniqueCategories.add(event.category);
      }
    });
    return Array.from(uniqueCategories).sort();
  }, [events]);

  // Filter events by category
  const filteredEvents = useMemo(() => {
    if (!selectedCategory) return events;
    
    if (selectedCategory === 'Today') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      
      return events.filter(event => {
        const dateStr = event.displayDate || event.date || event.event_date;
        if (!dateStr) return false;
        
        try {
          const eventDate = new Date(dateStr);
          const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
          return eventDateOnly.getTime() >= today.getTime() && eventDateOnly.getTime() < tomorrow.getTime();
        } catch (error) {
          return false;
        }
      });
    }
    
    if (selectedCategory === 'This Week') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sevenDaysFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      return events.filter(event => {
        const dateStr = event.displayDate || event.date || event.event_date;
        if (!dateStr) return false;
        
        try {
          const eventDate = new Date(dateStr);
          const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
          return eventDateOnly.getTime() >= today.getTime() && eventDateOnly.getTime() < sevenDaysFromNow.getTime();
        } catch (error) {
          return false;
        }
      });
    }
    
    return events.filter(event => event.category === selectedCategory);
  }, [events, selectedCategory]);

  // Get upcoming events (next 3)
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const sortedEvents = [...filteredEvents].sort((a, b) => {
      const dateA = new Date(a.displayDate || a.date || a.event_date || '').getTime();
      const dateB = new Date(b.displayDate || b.date || b.event_date || '').getTime();
      return dateA - dateB;
    });
    
    const futureEvents = sortedEvents.filter(event => {
      const dateStr = event.displayDate || event.date || event.event_date;
      if (!dateStr) return false;
      
      try {
        const eventDate = new Date(dateStr);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= now;
      } catch (error) {
        return false;
      }
    });
    
    return futureEvents.slice(0, 3);
  }, [filteredEvents]);

  // Get all events (excluding upcoming to avoid duplicates)
  const allEvents = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const sortedEvents = [...filteredEvents].sort((a, b) => {
      const dateA = new Date(a.displayDate || a.date || a.event_date || '').getTime();
      const dateB = new Date(b.displayDate || b.date || b.event_date || '').getTime();
      return dateA - dateB;
    });
    
    const futureEvents = sortedEvents.filter(event => {
      const dateStr = event.displayDate || event.date || event.event_date;
      if (!dateStr) return false;
      
      try {
        const eventDate = new Date(dateStr);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= now;
      } catch (error) {
        return false;
      }
    });
    
    const upcomingEventIds = new Set(upcomingEvents.map(e => e.id));
    return futureEvents.filter(event => !upcomingEventIds.has(event.id));
  }, [filteredEvents, upcomingEvents]);

  const scrollUpcoming = (direction) => {
    if (upcomingScrollRef.current) {
      const scrollAmount = 400;
      const currentScroll = upcomingScrollRef.current.scrollLeft;
      upcomingScrollRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getFilterDisplayName = () => {
    if (selectedCategory === 'Today') return 'today';
    if (selectedCategory === 'This Week') return 'this week';
    if (selectedCategory) return `for ${selectedCategory}`;
    return '';
  };

  const hasAnyEvents = upcomingEvents.length > 0 || allEvents.length > 0;

  // Empty state
  if (!events || events.length === 0) {
    return (
      <div className="empty-state">
        No upcoming events
      </div>
    );
  }

  // Render horizontal event card (upcoming section)
  const renderHorizontalCard = (event) => {
    const { date, time } = formatEventDateTime(event);
    const priceDisplay = getPriceDisplay(event);
    
    return (
      <div 
        key={event.id}
        className="horizontal-event-card"
        onClick={() => handleEventClick(event.id)}
      >
        <div className="horizontal-event-image">
          {event.cover_image_url && event.cover_image_url.trim() !== '' ? (
            <img 
              src={event.cover_image_url}
              alt={event.title}
              className="event-image"
            />
          ) : (
            <div className="event-gradient-placeholder">
              <span className="event-placeholder-emoji">📅</span>
            </div>
          )}
          <button 
            className="event-share-button"
            onClick={(e) => handleShareEvent(e, event.id)}
            aria-label="Share event"
          >
            <Upload size={16} />
          </button>
        </div>
        
        <div className="horizontal-event-title">
          {event.title}
        </div>
        
        <div className="horizontal-event-info">
          <div className="horizontal-event-left">
            <div className="horizontal-event-row">
              <Calendar size={16} className="event-icon" />
              <span className="horizontal-event-text">{date}</span>
            </div>
            <div className="horizontal-event-row">
              <Clock size={16} className="event-icon" />
              <span className="horizontal-event-text">{time}</span>
            </div>
          </div>
          
          {(event.requires_rsvp || event.requires_payment) && (
            <div className="horizontal-event-price">
              <div className="horizontal-event-rsvp-label">RSVP</div>
              <div className="horizontal-event-price-text">
                {event.requires_rsvp && !event.requires_payment ? 'Required' : priceDisplay}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render compact event card (all events section)
  const renderCompactCard = (event) => {
    const { date, time } = formatEventDateTime(event);
    const priceDisplay = getPriceDisplay(event);
    
    return (
      <div 
        key={event.id}
        className="compact-event-card"
        onClick={() => handleEventClick(event.id)}
      >
        <div className="compact-event-image">
          {event.cover_image_url && event.cover_image_url.trim() !== '' ? (
            <img 
              src={event.cover_image_url}
              alt={event.title}
              className="event-image"
            />
          ) : (
            <div className="event-gradient-placeholder">
              <span className="event-placeholder-emoji">📅</span>
            </div>
          )}
          <button 
            className="event-share-button"
            onClick={(e) => handleShareEvent(e, event.id)}
            aria-label="Share event"
          >
            <Upload size={14} />
          </button>
        </div>
        
        <div className="compact-event-content">
          <div className="compact-event-title">
            {event.title}
          </div>
          
          <div className="horizontal-event-info">
            <div className="horizontal-event-left">
              <div className="compact-event-row">
                <Calendar size={14} className="event-icon" />
                <span className="compact-event-text">{date}</span>
              </div>
              <div className="compact-event-row">
                <Clock size={14} className="event-icon" />
                <span className="compact-event-text">{time}</span>
              </div>
            </div>
            
            {(event.requires_rsvp || event.requires_payment) && (
              <div className="compact-event-price">
                <span className="compact-rsvp-label">RSVP</span>
                <span className="compact-price-text">
                  {event.requires_rsvp && !event.requires_payment ? 'Required' : priceDisplay}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="events-container">
      {/* Categories Filter */}
      <div className="events-categories-section">
        <div 
          className="events-categories-scroll"
          style={{ touchAction: 'pan-x' }}
        >
          <button
            className={`event-category-chip ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            All
          </button>
          
          <button
            className={`event-category-chip ${selectedCategory === 'Today' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('Today')}
          >
            Today
          </button>
          
          <button
            className={`event-category-chip ${selectedCategory === 'This Week' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('This Week')}
          >
            This Week
          </button>
          
          {categories.map((category) => (
            <button
              key={category}
              className={`event-category-chip ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state for filtered results */}
      {selectedCategory && !hasAnyEvents ? (
        <div className="events-empty-state">
          <div className="events-empty-text">
            Nothing happening {getFilterDisplayName()}
          </div>
        </div>
      ) : (
        <>
          {/* Upcoming Events Section */}
          {upcomingEvents.length > 0 && (
            <div className="upcoming-events-section">
              <div className="events-section-header">
                <h2 className="events-section-title">Upcoming Events</h2>
                {isInIframe && upcomingEvents.length > 1 && (
                  <div className="upcoming-scroll-arrows" style={{ display: 'flex' }}>
                    <button 
                      className="event-scroll-arrow"
                      onClick={() => scrollUpcoming('left')}
                      aria-label="Scroll left"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      className="event-scroll-arrow"
                      onClick={() => scrollUpcoming('right')}
                      aria-label="Scroll right"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </div>
              
              <div 
                className="upcoming-events-scroll"
                ref={upcomingScrollRef}
              >
                {upcomingEvents.map(event => renderHorizontalCard(event))}
              </div>
            </div>
          )}

          {/* All Events Section */}
          {allEvents.length > 0 && (
            <div className="all-events-section">
              <h2 className="events-section-title">All Events</h2>
              <div className="all-events-grid">
                {allEvents.map(event => renderCompactCard(event))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventsTab;
